import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Home, Mail, RefreshCw } from 'lucide-react';
import { trackPermissionDenied } from '../services/analyticsService';

interface PermissionDeniedProps {
  title: string;
  description: string;
  details?: string;
  /** Reason token used for telemetry (role | device | module | permission | service) */
  reason?: string;
  /** Optional recovery CTA shown below the standard ones */
  recoveryCta?: { label: string; to: string };
}

const PermissionDenied: React.FC<PermissionDeniedProps> = ({ title, description, details, reason, recoveryCta }) => {
  const location = useLocation();

  useEffect(() => {
    trackPermissionDenied(location.pathname, reason || 'unknown');
  }, [location.pathname, reason]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 px-4">
      <div className="w-full max-w-xl rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm p-4 md:p-5">
        <div className="flex items-start gap-4">
          <div className="rounded-xl bg-amber-100 dark:bg-amber-900/30 p-3 flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-amber-700 dark:text-amber-300" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">{title}</h1>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{description}</p>
            {details && (
              <p className="mt-3 text-xs font-mono bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-700 rounded-lg px-3 py-2 text-slate-500 dark:text-slate-400">
                {details}
              </p>
            )}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium px-4 py-2 transition-colors"
          >
            <Home className="w-4 h-4" />
            Go to dashboard
          </Link>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 text-sm font-medium px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
          <Link
            to="/profile"
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 text-sm font-medium px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            My profile
          </Link>
        </div>

        {recoveryCta && (
          <div className="mt-4">
            <Link
              to={recoveryCta.to}
              className="inline-flex items-center gap-2 text-teal-600 dark:text-teal-400 text-sm font-medium hover:underline"
            >
              <Mail className="w-4 h-4" />
              {recoveryCta.label}
            </Link>
          </div>
        )}

        <p className="mt-6 text-[11px] text-slate-400 dark:text-slate-500 text-center">
          If you believe this is an error, contact your system administrator or line manager.
        </p>
      </div>
    </div>
  );
};

export default PermissionDenied;
